import { SocialMedia, SocialMediaSearch } from "@shared/lib/types/influencer";
import { formatData } from "@shared/lib/utils/formatData";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import { Button, Dropdown } from "@shared/ui";
import { FileSpreadsheet, Plus, User, UserPlus } from "lucide-react";
import { ReactNode, useState } from "react";
import { AddCreatorBar } from "../CreatorFitTab/AddCreatorBar";
import { socialProfileUrl } from "../socialProfileUrl";
import styles from "./styles.module.scss";

type IncomingCreator = Partial<SocialMedia> & Partial<SocialMediaSearch>;

export const creatorRosterScroll = {
  y: "min(560px, calc(100vh - 260px))",
};

export function CreatorRosterAdd({
  meta,
  hint,
  showAdd,
  disabled,
  onSubmit,
  onImportSheet,
  children,
}: {
  meta: string;
  hint?: string;
  showAdd: boolean;
  disabled?: boolean;
  onSubmit: (creator: IncomingCreator) => void;
  onImportSheet: () => void;
  children: ReactNode;
}) {
  const [usernameOpen, setUsernameOpen] = useState(false);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Creators</h2>
          {showAdd && hint ? <p className={styles.hint}>{hint}</p> : null}
        </div>
        <div className={styles.actions}>
          <span className={styles.meta}>{meta}</span>
          {showAdd ? (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "username",
                    icon: <UserPlus size={14} />,
                    label: "Username",
                    onClick: () => setUsernameOpen(true),
                  },
                  {
                    key: "sheet",
                    icon: <FileSpreadsheet size={14} />,
                    label: "Upload sheet",
                    onClick: () => {
                      setUsernameOpen(false);
                      onImportSheet();
                    },
                  },
                ],
              }}
            >
              <Button size="small" icon={<Plus size={13} />} disabled={disabled}>
                Add
              </Button>
            </Dropdown>
          ) : null}
        </div>
      </div>
      {showAdd && usernameOpen ? (
        <div className={styles.usernamePanel}>
          <div className={styles.panelHead}>
            <span className={styles.panelLabel}>Username</span>
            <button
              type="button"
              className={styles.cancel}
              onClick={() => setUsernameOpen(false)}
            >
              Cancel
            </button>
          </div>
          <AddCreatorBar
            disabled={disabled}
            onSubmit={(creator) => {
              onSubmit(creator);
              setUsernameOpen(false);
            }}
          />
        </div>
      ) : null}
      {children}
    </>
  );
}

export function readFollowerCount(profile?: {
  followers?: number;
  searchV1?: { followerCount?: number };
}) {
  const count = profile?.followers ?? profile?.searchV1?.followerCount;
  return typeof count === "number" ? count : undefined;
}

export function FollowerCount({ count }: { count?: number }) {
  return (
    <span className={styles.followers}>
      {typeof count === "number" ? formatData(count) : "—"}
    </span>
  );
}

function UsernameLink({
  platform,
  username,
}: {
  platform?: string;
  username: string;
}) {
  const href = socialProfileUrl(platform, username);
  if (!href) return <span className={styles.handle}>@{username}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={styles.handle}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      @{username}
    </a>
  );
}

export function CreatorRosterIdentity({
  name,
  image,
  platform,
  username,
  linkProfile,
}: {
  name: string;
  image?: string;
  platform?: string;
  username?: string;
  linkProfile?: boolean;
}) {
  const handle = username?.replace(/^@+/, "") || "";
  const showHandle =
    Boolean(handle) && handle.toLowerCase() !== name.replace(/^@+/, "").toLowerCase();
  const profileHref = linkProfile ? socialProfileUrl(platform, handle) : undefined;

  return (
    <span className={styles.identity}>
      <span className={styles.avatarWrap}>
        {image ? (
          <img src={image} alt="" className={styles.avatar} />
        ) : (
          <span className={styles.avatarFallback}>
            <User size={14} />
          </span>
        )}
        {platform ? (
          <span className={styles.platformBadge}>{getPlatformIcon(platform)}</span>
        ) : null}
      </span>
      <span className={styles.identityCopy}>
        {profileHref && !showHandle ? (
          <a
            href={profileHref}
            target="_blank"
            rel="noreferrer"
            className={styles.nameLink}
            onClick={(event) => event.stopPropagation()}
          >
            {name}
          </a>
        ) : (
          <span className={styles.name}>{name}</span>
        )}
        {showHandle ? (
          <UsernameLink platform={platform} username={handle} />
        ) : null}
      </span>
    </span>
  );
}
